import { graphql } from 'gatsby';

import { styled } from '@mui/material';

import { ContentSlideData, ContentSlider } from '@mewa/ui';

import { markdownToHTML } from '../../../../libs/markdown-to-html';
import { ComponentProps, PossibleContentSections } from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';

export const ClaimTextFragment = graphql`
  fragment ContentSliderFragment on STRAPI__COMPONENT_SHARED_CONTENT_SLIDER {
    title: Title
    slides: Slide {
      linkIcon: LinkIcon
      linkText: LinkText
      linkUrl: LinkUrl
      subtitle: Subtitle
      title: Title
      content: Content {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
      linkPiwikEventName: LinkPiwikEventName
    }
    strapi_component
  }
`;

export const componentName = 'shared.content-slider';

export type ContentSliderData = {
  title: string | undefined;
  slides: ContentSlideData[];
  strapi_component: typeof componentName;
};

export type ContentSliderPreview = {
  strapi_component: typeof componentName;
  Title: string;
  Slide: {
    Title: string;
    Subtitle: string;
    Content: string;
    LinkIcon: string;
    LinkText: string;
    LinkUrl: string;
    LinkPiwikEventName: string;
  }[];
};

export const isContentSlider = (
  component: PossibleContentSections,
): component is ContentSliderData => {
  return component.strapi_component === componentName;
};

export const StrapiContentSlider = ({
  title,
  slides,
}: ComponentProps<ContentSliderData>) => {
  return (
    <Wrapper>
      <ContentSlider title={title} slides={slides} />
    </Wrapper>
  );
};

const Wrapper = styled('div')(() => ({ marginBottom: '75px' }));

export const config = {
  isComponent: isContentSlider,
  Component: StrapiContentSlider,
  componentName,
};

export const previewConfig: ContentLivePreviewConfig = {
  populationParams: { Slide: '*' },
  componentName,
  mapProperties: (slider): ContentSliderData | null =>
    slider.strapi_component === componentName
      ? {
          ...slider,
          title: slider.Title,
          slides: (slider as ContentSliderPreview).Slide.map((slide) => ({
            title: slide.Title,
            subtitle: slide.Subtitle,
            linkIcon: slide.LinkIcon,
            linkText: slide.LinkText,
            linkUrl: slide.LinkUrl,
            content: markdownToHTML(slide.Content),
            linkPiwikEventName: slide.LinkPiwikEventName,
          })),
        }
      : null,
};
